
package com.mps.asc_app.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.mps.asc_app.ui.theme.AscGoldLight

enum class UiSlotStatus { FREE, BOOKED, BLOCKED }
enum class BookingLayout { Auto, Vertical, Horizontal }

@Immutable
data class UiSlot(
    val id: String,
    val status: UiSlotStatus,
    val title: String? = null,
    val subtitle: String? = null,
    val participantNames: List<String> = emptyList(),
    val courtId: String? = null,
    val slotEpochMillis: Long? = null,
    val canCancel: Boolean = false,
    /** Wenn status == BLOCKED und ein Admin-Kommentar existiert */
    val blockedComment: String? = null,
    /** true, wenn der Slot durch Admin gesperrt ist (nicht "Abgelaufen") */
    val isAdminBlocked: Boolean = false,
    /** Nur für Admins: Sperre aufheben möglich */
    val canUnblock: Boolean = false,
)

@Immutable
data class UiRow(
    val timeLabel: String, // e.g. "14:00"
    val left: UiSlot,      // Court 1
    val right: UiSlot,     // Court 2
)

@Immutable
data class UiMonthDay(
    val dayOfMonth: Int,
    val isInCurrentMonth: Boolean,
    val hasBookings: Boolean = false,
)

// -----------------------------
// Finished booking calendar component
// Left: Month calendar
// Right: Slots for selected day
// UI-only. Caller controls state + provides rows for selected day.
// Responsive: switches to vertical layout on narrow screens.
// -----------------------------
@Composable
fun BookingCalendarComponent(
    year: Int,
    month: Int, // 1..12
    selectedDay: Int?,
    onPrevMonth: () -> Unit,
    onNextMonth: () -> Unit,
    onDayClick: (Int) -> Unit,

    courts: List<String>,
    rows: List<UiRow>,
    onSlotClick: (UiSlot) -> Unit,

    modifier: Modifier = Modifier,
    /**
     * Wenn true, verwendet die Komponente kein eigenes Scroll (Slots-Liste ohne LazyColumn)
     * und vermeidet fillMaxSize/weight, damit ein übergeordnetes Layout (z.B. BookingScreen)
     * die komplette Seite scrollen kann.
     */
    embedInParentScroll: Boolean = false,
    leftWidth: Dp = 360.dp,
    layout: BookingLayout = BookingLayout.Auto,
    title: String? = null,
) {

    BoxWithConstraints(modifier = modifier.fillMaxWidth()) {
        val autoVertical = maxWidth < 1100.dp  // bewusst höher, damit Mobile zuverlässig triggert

        val useVertical = when (layout) {
            BookingLayout.Vertical -> true
            BookingLayout.Horizontal -> false
            BookingLayout.Auto -> autoVertical
        }

        if (useVertical) {
            Column(Modifier.fillMaxWidth()) {
                MonthCalendar(
                    year = year,
                    month = month,
                    selectedDay = selectedDay,
                    onPrevMonth = onPrevMonth,
                    onNextMonth = onNextMonth,
                    onDayClick = onDayClick,
                    title = title,
                    modifier = Modifier
                        .fillMaxWidth()
                        .wrapContentHeight(),
                )

                HorizontalDivider()

                DaySlotsPanel(
                    year = year,
                    month = month,
                    selectedDay = selectedDay,
                    courts = courts,
                    rows = rows,
                    onSlotClick = onSlotClick,
                    embedInParentScroll = embedInParentScroll,
                    modifier = Modifier
                        .fillMaxWidth(),
                )
            }
        } else {
            Row(Modifier.fillMaxWidth()) {
                Column(
                    Modifier
                        .width(leftWidth)
                        .wrapContentHeight()
                ) {
                    MonthCalendar(
                        year = year,
                        month = month,
                        selectedDay = selectedDay,
                        onPrevMonth = onPrevMonth,
                        onNextMonth = onNextMonth,
                        onDayClick = onDayClick,
                        title = title,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }

                VerticalDivider()

                DaySlotsPanel(
                    year = year,
                    month = month,
                    selectedDay = selectedDay,
                    courts = courts,
                    rows = rows,
                    onSlotClick = onSlotClick,
                    embedInParentScroll = embedInParentScroll,
                    modifier = Modifier.weight(1f),
                )
            }
        }
    }
}

// -----------------------------
// Left side: Month calendar
// -----------------------------
@Composable
fun MonthCalendar(
    year: Int,
    month: Int, // 1..12
    selectedDay: Int?,
    onPrevMonth: () -> Unit,
    onNextMonth: () -> Unit,
    onDayClick: (dayOfMonth: Int) -> Unit,
    modifier: Modifier = Modifier,
    title: String? = null,
) {
    Column(modifier) {
        Surface(tonalElevation = 2.dp) {
            Row(
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 12.dp, vertical = 10.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                IconButton(onClick = onPrevMonth) { Text("‹") }
                Column(Modifier.weight(1f), horizontalAlignment = Alignment.CenterHorizontally) {
                    if (!title.isNullOrBlank()) {
                        Text(title, style = MaterialTheme.typography.titleLarge)
                    }
                    Text(text = monthTitle(year, month), style = MaterialTheme.typography.bodySmall)
                }
                IconButton(onClick = onNextMonth) { Text("›") }
            }
        }

        HorizontalDivider()

        WeekdayHeaderRow(Modifier.fillMaxWidth())

        val days = remember(year, month) { buildMonthGrid(year, month) }
        MonthGrid(
            days = days,
            selectedDay = selectedDay,
            onDayClick = onDayClick,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 8.dp, vertical = 6.dp)
        )
    }
}

@Composable
private fun WeekdayHeaderRow(modifier: Modifier = Modifier) {
    Row(
        modifier
            .fillMaxWidth()
            .padding(horizontal = 8.dp, vertical = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(4.dp, Alignment.CenterHorizontally)
    ) {
        listOf("Mo", "Di", "Mi", "Do", "Fr", "Sa", "So").forEach { label ->
            Box(
                modifier = Modifier.size(36.dp),  // exakt wie DayCell
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = label,
                    style = MaterialTheme.typography.labelMedium,
                    maxLines = 1
                )
            }
        }
    }
}

@Composable
private fun MonthGrid(
    days: List<UiMonthDay>,
    selectedDay: Int?,
    onDayClick: (Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier) {
        for (week in 0 until 6) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(4.dp, Alignment.CenterHorizontally)
            ) {
                for (dow in 0 until 7) {
                    val cell = days[week * 7 + dow]
                    val isSelected = cell.isInCurrentMonth && selectedDay == cell.dayOfMonth

                    DayCell(
                        day = cell,
                        isSelected = isSelected,
                        onClick = {
                            if (cell.isInCurrentMonth) onDayClick(cell.dayOfMonth)
                        },
                        modifier = Modifier.size(36.dp)
                    )
                }
            }
            Spacer(Modifier.height(4.dp))
        }
    }
}

@Composable
private fun DayCell(
    day: UiMonthDay,
    isSelected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val shape = MaterialTheme.shapes.medium

    // Ausgewaehlter Tag: Vereinsblau mit goldenem Punkt (wie im Entwurf).
    val (bg, border) = when {
        isSelected -> MaterialTheme.colorScheme.primary to null
        !day.isInCurrentMonth -> MaterialTheme.colorScheme.surfaceVariant to null
        else -> MaterialTheme.colorScheme.surface to BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    }
    val dayColor = when {
        isSelected -> MaterialTheme.colorScheme.onPrimary
        day.isInCurrentMonth -> MaterialTheme.colorScheme.onSurface
        else -> MaterialTheme.colorScheme.onSurfaceVariant
    }

    Surface(
        modifier = modifier.clickable(enabled = day.isInCurrentMonth, onClick = onClick),
        shape = shape,
        color = bg,
        border = border,
        tonalElevation = 0.dp
    ) {
        Box(Modifier.fillMaxSize().padding(4.dp)) {
            Text(
                text = day.dayOfMonth.toString(),
                style = MaterialTheme.typography.bodySmall,
                color = dayColor,
                modifier = Modifier.align(Alignment.TopStart)
            )

            if (day.hasBookings && day.isInCurrentMonth) {
                Text(
                    text = "•",
                    style = MaterialTheme.typography.labelMedium,
                    color = if (isSelected) AscGoldLight else MaterialTheme.colorScheme.secondary,
                    modifier = Modifier.align(Alignment.BottomEnd)
                )
            }
        }
    }
}

// -----------------------------
// Right side: slots panel
// -----------------------------
@Composable
private fun DaySlotsPanel(
    year: Int,
    month: Int,
    selectedDay: Int?,
    courts: List<String>,
    rows: List<UiRow>,
    onSlotClick: (UiSlot) -> Unit,
    embedInParentScroll: Boolean,
    modifier: Modifier = Modifier,
) {
    Column(modifier.padding(12.dp)) {
        Text(
            text = if (selectedDay == null) "Bitte Datum wählen" else "Slots am $selectedDay.$month.$year",
            style = MaterialTheme.typography.titleMedium,
        )

        Spacer(Modifier.height(8.dp))

        if (selectedDay == null) {
            Text(
                text = "Wähle links ein Datum aus, dann erscheinen hier alle Slots.",
                style = MaterialTheme.typography.bodyMedium,
            )
            Spacer(Modifier.height(8.dp))
        }

        // Always show the grid header (courts). Rows may be empty until a date is selected.
        TwoCourtSlotsGrid(
            courts = courts,
            rows = if (selectedDay == null) emptyList() else rows,
            onSlotClick = onSlotClick,
            scrollable = !embedInParentScroll,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

// -----------------------------
// Month helpers (pure, no datetime)
// -----------------------------
private fun monthTitle(year: Int, month: Int): String {
    val m = when (month) {
        1 -> "Januar"
        2 -> "Februar"
        3 -> "März"
        4 -> "April"
        5 -> "Mai"
        6 -> "Juni"
        7 -> "Juli"
        8 -> "August"
        9 -> "September"
        10 -> "Oktober"
        11 -> "November"
        12 -> "Dezember"
        else -> "Monat"
    }
    return "$m $year"
}

private fun isLeapYear(year: Int): Boolean =
    (year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)

private fun daysInMonth(year: Int, month: Int): Int = when (month) {
    1, 3, 5, 7, 8, 10, 12 -> 31
    4, 6, 9, 11 -> 30
    2 -> if (isLeapYear(year)) 29 else 28
    else -> 30
}

// ISO day number: Monday=1..Sunday=7
// Uses Zeller congruence (Gregorian)
private fun isoDayOfWeek(year: Int, month: Int, day: Int): Int {
    var y = year
    var m = month
    if (m < 3) {
        m += 12
        y -= 1
    }
    val k = y % 100
    val j = y / 100
    val h = (day + (13 * (m + 1)) / 5 + k + (k / 4) + (j / 4) + (5 * j)) % 7
    // Zeller: 0=Saturday,1=Sunday,2=Monday,...6=Friday
    return when (h) {
        0 -> 6
        1 -> 7
        2 -> 1
        3 -> 2
        4 -> 3
        5 -> 4
        6 -> 5
        else -> 1
    }
}

private fun buildMonthGrid(year: Int, month: Int): List<UiMonthDay> {
    val firstDow = isoDayOfWeek(year, month, 1) // 1..7 (Mon..Sun)
    val dim = daysInMonth(year, month)

    val prevMonth = if (month == 1) 12 else month - 1
    val prevYear = if (month == 1) year - 1 else year
    val prevDim = daysInMonth(prevYear, prevMonth)

    val leading = firstDow - 1
    val totalCells = 42
    val out = ArrayList<UiMonthDay>(totalCells)

    for (i in 0 until leading) {
        val dom = prevDim - leading + 1 + i
        out += UiMonthDay(dayOfMonth = dom, isInCurrentMonth = false)
    }

    for (dom in 1..dim) {
        out += UiMonthDay(dayOfMonth = dom, isInCurrentMonth = true)
    }

    while (out.size < totalCells) {
        val dom = out.size - (leading + dim) + 1
        out += UiMonthDay(dayOfMonth = dom, isInCurrentMonth = false)
    }

    return out
}

